import api from 'api';
import Checkbox from 'components/controls/checkbox';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'components/modal';
import moment, { Moment } from 'moment';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClientContactType, RootState } from 'types';
import './notification-management-modal.scss';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { SmallAlert } from 'types/alert';

function NotificationManagementModal({
    open,
    setOpen
} : {
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>
}) {
    const params: any = useParams();
    const project = useSelector((state: RootState) => state.project);
    const [contacts, setContacts] = useState<ClientContactType[]>([]);
    const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
    const [interval, setInterval] = useState<number>(0);
    const [startDate, setStartDate] = useState<string>(moment().format("YYYY-MM-DD"));
    const [startTime, setStartTime] = useState<string>(moment().format("HH:mm"));
    const [showList, setShowList] = useState(false);
    const [notificationList, setNotificationList] = useState<SmallAlert[]>([]);

    const getContacts = useCallback(async () => {
        const data = await api.clientContact.getAll(params.clientId);
        setContacts(data);
    }, [params.clientId])

    const getAlerts = useCallback(async () => {
        const data = await api.alert.getAll(params.projectId);
        setNotificationList(data);
    }, [params.projectId])

    const getIntervalString = (interval: number) => {
        return {
            0: 'Immediate',
            5: 'Every 5 mins',
            10: 'Every 10 mins',
            15: 'Every 15 mins',
            30: 'Event 30 mins',
            60: 'Every hour',
            360: 'Every 6 hours',
            720: 'Every 12 hours',
            1440: 'Once a Day'
        }[interval] || '';
    }

    const toggleContact = (id: number) => {
        if (selectedContacts.includes(id)) {
            setSelectedContacts(selectedContacts.filter(x => x !== id));
        } else {
            setSelectedContacts([ ...selectedContacts, id ])
        }
    } 

    const deleteNotification = async (id: number) => {
        await api.alert.delete(id)
        .then(data => {
            toast.success("Alert Deleted");
        })
        .catch(err => {
            toast.error(err.toString());
        })
        .finally(
            () => setNotificationList(notificationList.filter((x: any) => x.Id !== id))
        );        
    }

    const save = async () => {
        if (!selectedContacts.length) {
            toast.warning('Please select at least 1 recipient');
            return;
        } else {
            await api.alert.save(
                `${project.Description} - Alert`, 
                project.Id, 
                selectedContacts, 
                interval, 
                moment(startDate).set('hours', parseInt(startTime.split(':')[0])).set('minutes', parseInt(startTime.split(':')[1]))
            )
            .then(data => {
                setNotificationList([...notificationList, { Id: data, Interval: interval, RecipientCount: selectedContacts.length }])
                toast.success("Alert Scheduled");
            })
            .catch(err => {
                toast.error(err.toString());
            })
            .finally(
                () => setOpen(false)
            )
        }
    }

    useEffect(() => {
        getContacts();
        getAlerts();
    }, [getContacts, getAlerts])

    return (
        <Modal id="notification-management-modal" open={open} setOpen={setOpen}>
            <ModalHeader>
                <p>Notification Management</p>
            </ModalHeader>
            <ModalBody>
                {showList ? (
                    <>
                        <table>
                            <thead>
                                <tr>
                                    <th>Interval</th>
                                    <th>Recipients</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {!notificationList.length && 
                                    <tr>
                                        <td colSpan={3}><i style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>No alerts created</i></td>
                                    </tr>
                                }
                                {notificationList.map((x: any) => (
                                    <tr>
                                        <td>{getIntervalString(x.Interval)}</td>
                                        <td style={{ textAlign: 'center' }}>{x.RecipientCount}</td>
                                        <td style={{ width: '50px' }}><i className="fal fa-trash-alt" onClick={() => deleteNotification(x.Id)}></i></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                ) : (
                    <>
                        <p>Please indicate which contacts should receive alerts from amaSocial.</p>
                        {contacts.map((x, i) => (
                            <div className="contact" key={i}>
                                <label htmlFor={`contact-${x.Id}`}>{x.Name} {x.Surname} <span>({x.Email})</span></label>
                                <Checkbox 
                                    id={`contact-${x.Id}`} 
                                    checked={selectedContacts.includes(x.Id)}
                                    onChange={() => toggleContact(x.Id)}
                                />
                            </div>
                        ))}
                        <p style={{ marginTop: 25, marginBottom: 15 }}>When should these alerts be delivered?</p>
                        <select 
                            value={interval} 
                            onChange={e => setInterval(parseInt(e.currentTarget.value))}
                            style={{ width: '100%', height: 40, marginTop: 0, fontSize: 16 }}
                        >
                            <option value={0}>Immediate</option>
                            <option value={5}>Every 5 mins</option>
                            <option value={10}>Every 10 mins</option>
                            <option value={15}>Every 15 mins</option>
                            <option value={30}>Every 30 mins</option>
                            <option value={60}>Every hour</option>
                            <option value={360}>Every 6 hours</option>
                            <option value={720}>Every 12 hours</option>
                            <option value={1440}>Once a day</option>
                        </select>
                        <p style={{ marginTop: 15, marginBottom: 5 }}>Starting at:</p>
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={e => setStartDate(e.currentTarget.value)} 
                            style={{ width: '100%', height: 40, fontSize: 16 }}
                        />
                        <input 
                            type="time" 
                            value={startTime} 
                            onChange={e => setStartTime(e.currentTarget.value)} 
                            style={{ width: '100%', height: 40, fontSize: 16, marginTop: 10 }}
                        />
                    </>
                )}
                
            </ModalBody>
            <ModalFooter>
                <button onClick={() => setOpen(false)}>Cancel</button>
                {!showList && <button onClick={save}>Done</button>}
                <button onClick={() => setShowList(!showList)}>{showList ? 'New' : 'List'}</button>
            </ModalFooter>
        </Modal>
    )
}

export default NotificationManagementModal;